import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
  makeStyles
} from '@material-ui/core';
import { useQueryState } from 'nuqs';
import { useSnackbar } from 'notistack';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Eye as EyeIcon } from 'react-feather';
import ContextMenu, { ContextMenuLinks } from 'src/components/ContextMenu';
import DataTile from 'src/components/DataTile';
import MUIReactTable, {
  SelectColumnFilter
} from 'src/components/MUIReactTable';
import Page from 'src/components/Page';
import ResponsiveDialog from 'src/components/ResponsiveDialog';
import Truncate from 'src/components/Truncate';
import { uiConfig } from 'src/config';
import {
  useGetCashReportStationsQuery,
  useLazyGetCashTransactionSummaryQuery,
  useLazyGetCashTransactionsQuery
} from 'src/features/cashTransactionReport/api/cashTransactionReportApi';
import {
  buildCashTransactionFilters,
  formatCurrency
} from 'src/features/cashTransactionReport/utils/cashTransactionReport';
import {
  CalendarDate,
  calendarRangeToInstants,
  DateTime,
  parseAsCalendarDate,
  todayIn,
  useTimezone
} from 'src/features/timezone';
import { useStores } from 'src/features/store';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import useAppInsightPageView from 'src/hooks/useAppInsightPageView';
import useCommonStyles from 'src/hooks/useCommonStyles';
import useSettings from 'src/hooks/useSettings';
import { Theme } from 'src/theme';
import { DataDTOTransactionHistory } from 'src/types/api/generated/api';
import { Resources } from 'src/types/resources';
import { getSelectedClientId } from 'src/utils/client';
import { buildOrderByString } from 'src/utils/tableQuery';
import CashTransactionDetail from './CashTransactionDetail';
import Header from './Header';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  }
}));

const CashTransactionReportPage: FC = () => {
  useAppInsightPageView({ name: 'ExoAdmin-Cash Transactions' });
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { enqueueSnackbar } = useSnackbar();
  const clientId = getSelectedClientId();
  const { zone } = useTimezone();
  const [rawStartDate, setStartDate] = useQueryState(
    'startDate',
    parseAsCalendarDate
  );
  const [rawEndDate, setEndDate] = useQueryState(
    'endDate',
    parseAsCalendarDate
  );
  const today = todayIn(zone);
  const startDate = rawStartDate ?? today.subtract({ weeks: 1 });
  const endDate = rawEndDate ?? today;
  const { startISO, endISO } = calendarRangeToInstants(
    startDate,
    endDate,
    zone
  );

  const [
    selectedTransaction,
    setSelectedTransaction
  ] = useState<DataDTOTransactionHistory | null>(null);
  const { settings } = useSettings();
  const { tables = { sizes: {} } } = settings;
  const {
    sizes: { [Resources.cashTransactionReport]: savedRowsPerPage }
  } = tables;
  const previousPageValues = useRef({
    pageSize: savedRowsPerPage || uiConfig.listSize || 10,
    pageIndex: 0,
    filters: [],
    sortBy: []
  });

  const [
    getCashTransactions,
    {
      data: transactions = {
        page: -1,
        totalPages: 1,
        totalCount: 1,
        rows: [],
        pageSize: 1,
        skip: 0
      },
      isFetching: isFetchingTransactions,
      error: transactionsError
    }
  ] = useLazyGetCashTransactionsQuery();
  const [
    getCashTransactionSummary,
    {
      data: summary = { totalCashCollected: 0, itemsPurchased: 0 },
      isFetching: isFetchingSummary,
      error: summaryError
    }
  ] = useLazyGetCashTransactionSummaryQuery();
  const { stores, isFetchingStores } = useStores();
  const {
    data: stations = { totalPages: 0, totalCount: 0, rows: [] },
    isFetching: isFetchingStations,
    error: stationsError
  } = useGetCashReportStationsQuery(
    { page: 0, pageSize: 0, orderBy: 'name ASC', clientId },
    { skip: !clientId }
  );

  const reportError = transactionsError ?? summaryError ?? stationsError;
  const reportErrorMessage = useApiErrorMessage(reportError);

  useEffect(() => {
    if (reportError && reportErrorMessage) {
      enqueueSnackbar(reportErrorMessage, {
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  }, [enqueueSnackbar, reportError, reportErrorMessage]);

  const fetchRecords = useCallback(
    ({ pageSize, pageIndex, filters, sortBy }): void => {
      previousPageValues.current = { pageSize, pageIndex, filters, sortBy };
      if (!clientId) {
        return;
      }

      const transactionFilters = buildCashTransactionFilters(
        filters,
        startISO,
        endISO
      );
      getCashTransactions(
        {
          clientId,
          page: pageIndex + 1,
          pageSize: Number(pageSize),
          filters: transactionFilters,
          orderBy: buildOrderByString(sortBy)
        },
        true
      );
      getCashTransactionSummary(
        { clientId, filters: transactionFilters },
        true
      );
    },
    [clientId, endISO, getCashTransactionSummary, getCashTransactions, startISO]
  );

  const initialRender = useRef(true);
  useEffect(() => {
    if (!initialRender.current) {
      fetchRecords(previousPageValues.current);
    } else {
      initialRender.current = false;
    }
  }, [fetchRecords]);

  const handleDateRangeChange = (
    start: CalendarDate,
    end: CalendarDate
  ): void => {
    setStartDate(start);
    setEndDate(end);
  };

  const getColumns = () => [
    {
      Header: 'Store',
      accessor: 'storeId',
      filterable: true,
      Filter: SelectColumnFilter,
      filter: 'includes',
      options: stores.map(store => ({
        id: store.id,
        name: <Truncate value={store.name ?? ''} length={50} />
      })),
      sortable: false
    },
    {
      Header: 'Station',
      accessor: 'stationId',
      filterable: true,
      Filter: SelectColumnFilter,
      filter: 'includes',
      options: stations.rows.map(station => ({
        id: station.id,
        name: <Truncate value={station.name ?? ''} length={50} />
      })),
      sortable: false
    },
    {
      Header: 'External ID',
      accessor: 'externalId',
      filterable: true,
      sortable: false
    },
    {
      Header: 'First Name',
      accessor: 'cashierFirstName',
      filterable: true,
      sortable: false
    },
    {
      Header: 'Last Name',
      accessor: 'cashierLastName',
      filterable: true,
      sortable: false
    },
    {
      Header: 'Start Time',
      accessor: 'startedAt',
      filterable: false,
      sortable: false
    },
    {
      Header: 'Items Purchased',
      accessor: 'validItemCount',
      filterable: false,
      sortable: false
    },
    {
      Header: 'Amount / Total Price',
      accessor: 'cashCollectedAmount',
      filterable: false,
      sortable: false
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      filterable: false,
      sortable: false
    }
  ];

  const tableData = transactions.rows.map(transaction => {
    const contextMenuLinks: ContextMenuLinks[] = [
      {
        to: false,
        onClick: () => setSelectedTransaction(transaction),
        title: 'View Transaction',
        icon: () => <EyeIcon size={20} />,
        show: true
      }
    ];
    return {
      storeId: <Truncate value={transaction.storeName ?? ''} />,
      stationId: <Truncate value={transaction.stationName ?? ''} />,
      externalId: <Truncate value={transaction.externalId ?? ''} />,
      cashierFirstName: <Truncate value={transaction.cashierFirstName ?? ''} />,
      cashierLastName: <Truncate value={transaction.cashierLastName ?? ''} />,
      startedAt: <DateTime value={transaction.startedAt} />,
      validItemCount: transaction.validItemCount,
      cashCollectedAmount: formatCurrency(transaction.cashCollectedAmount),
      actions: <ContextMenu links={contextMenuLinks} />
    };
  });

  return (
    <>
      <ResponsiveDialog
        title="Cash Transaction Detail"
        showTitle={false}
        open={Boolean(selectedTransaction)}
        actions={<></>}
        fullWidth={true}
        showCloseButton={true}
        maxWidth="lg"
        handleClose={() => setSelectedTransaction(null)}
      >
        {selectedTransaction ? (
          <CashTransactionDetail transaction={selectedTransaction} />
        ) : (
          <></>
        )}
      </ResponsiveDialog>
      <Page className={classes.root} title="Cash Transactions" exoBgLogo={true}>
        <Backdrop
          className={commonClasses.backdrop}
          open={isFetchingStores || isFetchingStations}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Container maxWidth={false}>
          <Header
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
          />
          <Box mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DataTile
                  title="Total Cash Collected"
                  data={formatCurrency(summary.totalCashCollected)}
                  loading={isFetchingSummary}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DataTile
                  title="Items Purchased"
                  data={summary.itemsPurchased}
                  loading={isFetchingSummary}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={3}>
            <MUIReactTable
              name={Resources.cashTransactionReport}
              loading={false}
              data={tableData}
              columns={getColumns()}
              requestData={fetchRecords}
              manual={true}
              pageCount={transactions.totalPages}
              totalCount={transactions.totalCount}
              noRecords={Boolean(
                (transactions.page ?? -1) > 0 && !transactions.rows.length
              )}
              refreshing={isFetchingTransactions}
            />
          </Box>
        </Container>
      </Page>
    </>
  );
};

export default CashTransactionReportPage;
